// src/utils/timelineTriggerEngine.js
// ============================================================================
// TIMELINE TRIGGER ENGINE
// ============================================================================
// Single source of truth for all timeline workflow trigger decisions.
//
// Every timeline date change in the app must call resolveTimelineTrigger()
// to determine whether a workflow should fire. No trigger logic should exist
// outside this module.
//
// TRIGGER MATRIX (standard stages):
//   ┌──────────────────────┬───────────────┬──────────────────┬────────────────────┐
//   │ Field                │ First entry   │ Modification     │ Email status       │
//   ├──────────────────────┼───────────────┼──────────────────┼────────────────────┤
//   │ contractDate         │ passive       │ passive          │ Not Applicable     │
//   │ clientApprovalDate   │ passive       │ passive          │ Not Applicable     │
//   │ targetDate (planned) │ passive       │ timeline_change* │ N/A or active      │
//   │ actualDate           │ stage_closure │ no re-trigger    │ Pending→Draft→Sent │
//   │ proformaInvoiceDate  │ proforma_inv  │ no re-trigger    │ Pending→Draft→Sent │
//   │ invoiceDate          │ invoice_sent  │ no re-trigger    │ Pending→Draft→Sent │
//   │ paidDate (advance)   │ advance_paid  │ no re-trigger    │ Pending→Draft→Sent │
//   │ paidDate (others)    │ invoice_paid  │ no re-trigger    │ Pending→Draft→Sent │
//   └──────────────────────┴───────────────┴──────────────────┴────────────────────┘
//   * targetDate timeline_change only fires if delta exceeds threshold (caller-managed)
//
// ROUTING:
//   native_mail  — opens the supervisor's local mail client (project-stage comms)
//   radar        — creates a RADAR notification for admin (invoice/admin comms)
//
//   native_mail: timeline_change, stage_closure, advance_paid, invoice_paid
//   radar:       proforma_invoice_created, invoice_sent
//
// CC (Construction Coordination) stages — invoice-only workflow:
//   ┌──────────────────────┬───────────────┬──────────────────┬────────────────────┐
//   │ Field                │ First entry   │ Modification     │ Email status       │
//   ├──────────────────────┼───────────────┼──────────────────┼────────────────────┤
//   │ invoiceDate          │ invoice_sent  │ no re-trigger    │ Pending→Draft→Sent │
//   │ paidDate             │ passive       │ passive          │ Pending→Draft→Sent │
//   │ (all other fields)   │ passive       │ passive          │ Not Applicable     │
//   └──────────────────────┴───────────────┴──────────────────┴────────────────────┘
//   CC stages only support: tax invoice creation → RADAR notification.
//   CC must NOT trigger stage initiation, stage closure, or timeline change emails.
//
// SAFETY: This function must only be called from genuine user-edit handlers.
// Never call during hydration, mount effects, migration, or normalization.
// ============================================================================

// ── Trigger type constants ──────────────────────────────────────────────────

export const TRIGGER_TYPES = {
  TIMELINE_CHANGE:          'timeline_change',
  STAGE_CLOSURE:            'stage_closure',
  PROFORMA_INVOICE_CREATED: 'proforma_invoice_created',
  INVOICE_SENT:             'invoice_sent',
  ADVANCE_PAID:             'advance_paid',
  INVOICE_PAID:             'invoice_paid',
};

// ── Trigger routing: determines how a trigger is dispatched ──────────────────
// native_mail = open supervisor's local mail client (project-stage comms)
// radar       = create RADAR notification/task for admin (invoice/admin comms)

export const TRIGGER_ROUTING = {
  [TRIGGER_TYPES.TIMELINE_CHANGE]:          'native_mail',
  [TRIGGER_TYPES.STAGE_CLOSURE]:            'native_mail',
  [TRIGGER_TYPES.PROFORMA_INVOICE_CREATED]: 'radar',
  [TRIGGER_TYPES.INVOICE_SENT]:             'radar',
  [TRIGGER_TYPES.ADVANCE_PAID]:             'native_mail',
  [TRIGGER_TYPES.INVOICE_PAID]:             'native_mail',
};

/**
 * Returns the routing channel for a given trigger type.
 * @param {string} triggerType - One of TRIGGER_TYPES values
 * @returns {'native_mail'|'radar'|null}
 */
export function getTriggerRouting(triggerType) {
  return TRIGGER_ROUTING[triggerType] || null;
}

// ── CC stage detection ───────────────────────────────────────────────────────
// CC stages use key 'cc' or 'cc__YYYY-MM' (monthly retainer sub-stages).
// They only support the invoice_sent trigger — no stage initiation/closure.

export function isCCStage(stageKey) {
  return stageKey === 'cc' || (typeof stageKey === 'string' && stageKey.startsWith('cc__'));
}

// ── Fields that are always passive (never trigger workflows) ────────────────

const PASSIVE_FIELDS = new Set(['contractDate', 'clientApprovalDate']);

// ── Fields that have active workflow triggers ───────────────────────────────

const ACTIVE_FIELDS = new Set(['actualDate', 'proformaInvoiceDate', 'invoiceDate', 'paidDate']);

// ── Helper: is a value empty (null, undefined, or empty string)? ────────────

function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

// ── Planned date threshold check ────────────────────────────────────────────

/**
 * Check if a planned date change exceeds the threshold.
 * @param {string} previousDate - YYYY-MM-DD
 * @param {string} nextDate - YYYY-MM-DD
 * @param {number} thresholdDays - Minimum delta to trigger email (default: 7)
 * @returns {boolean}
 */
export function doesPlannedDateExceedThreshold(previousDate, nextDate, thresholdDays = 7) {
  if (!previousDate || !nextDate) return false;
  const prevMs = new Date(previousDate).getTime();
  const nextMs = new Date(nextDate).getTime();
  const deltaDays = Math.abs(Math.round((nextMs - prevMs) / 86400000));
  return deltaDays > thresholdDays;
}

// ── Core trigger resolver ───────────────────────────────────────────────────

/**
 * Determine the workflow trigger for a timeline date change.
 *
 * @param {Object} params
 * @param {string} params.fieldKey       - The timeline field being changed
 * @param {string|null} params.previousValue - Value before the change
 * @param {string|null} params.nextValue     - Value after the change
 * @param {string} params.stageKey       - The stage key (e.g. 'advance', 'cd_ad')
 * @param {number} [params.plannedDateThreshold] - Optional threshold days for targetDate trigger
 *
 * @returns {string|null} One of the TRIGGER_TYPES values, or null for no trigger
 */
export function resolveTimelineTrigger({ fieldKey, previousValue, nextValue, stageKey, plannedDateThreshold }) {
  // Normalize: treat empty strings as null for comparison
  const prev = isEmpty(previousValue) ? null : previousValue;
  const next = isEmpty(nextValue) ? null : nextValue;

  // No change → no trigger
  if (prev === next) return null;

  // Clearing a value → no trigger
  if (next === null) return null;

  // ── Passive fields: always null ─────────────────────────────────
  if (PASSIVE_FIELDS.has(fieldKey)) return null;

  // ── CC stages: invoice-only workflow ───────────────────────────
  // CC supports ONLY invoice_sent (tax invoice creation → RADAR).
  // All other fields are passive for CC — no stage initiation/closure.
  if (isCCStage(stageKey)) {
    if (fieldKey === 'invoiceDate' && prev === null) {
      return TRIGGER_TYPES.INVOICE_SENT;
    }
    return null; // all other CC fields are passive
  }

  // ── targetDate (planned date) ───────────────────────────────────
  // First entry (null → value) = passive
  // Modification (value → different value) = timeline_change
  //   BUT only if delta exceeds the configurable threshold
  if (fieldKey === 'targetDate') {
    if (prev === null) return null; // first entry → passive
    // Check threshold: if provided, only trigger if delta exceeds it
    const threshold = plannedDateThreshold != null ? plannedDateThreshold : 7;
    if (!doesPlannedDateExceedThreshold(prev, next, threshold)) return null;
    return TRIGGER_TYPES.TIMELINE_CHANGE;
  }

  // ── actualDate ──────────────────────────────────────────────────
  // First entry only triggers stage_closure
  if (fieldKey === 'actualDate') {
    if (prev === null) return TRIGGER_TYPES.STAGE_CLOSURE;
    return null; // modification → no re-trigger
  }

  // ── proformaInvoiceDate ────────────────────────────────────────
  // First entry only triggers proforma_invoice_created → RADAR notification
  if (fieldKey === 'proformaInvoiceDate') {
    if (prev === null) return TRIGGER_TYPES.PROFORMA_INVOICE_CREATED;
    return null; // modification → no re-trigger
  }

  // ── invoiceDate ─────────────────────────────────────────────────
  // First entry only triggers invoice_sent → RADAR notification
  if (fieldKey === 'invoiceDate') {
    if (prev === null) return TRIGGER_TYPES.INVOICE_SENT;
    return null; // modification → no re-trigger
  }

  // ── paidDate ────────────────────────────────────────────────────
  // First entry only triggers payment workflow
  // Advance stage → advance_paid (project_initiated)
  // All other stages → invoice_paid (stage_initiated)
  if (fieldKey === 'paidDate') {
    if (prev === null) {
      return stageKey === 'advance'
        ? TRIGGER_TYPES.ADVANCE_PAID
        : TRIGGER_TYPES.INVOICE_PAID;
    }
    return null; // modification → no re-trigger
  }

  // Unknown field → no trigger
  return null;
}

// ── Email status derivation ─────────────────────────────────────────────────

/**
 * Determine if a field is passive (should show "Not Applicable" for email status).
 *
 * @param {string} fieldKey - The timeline field name
 * @returns {boolean}
 */
export function isFieldPassive(fieldKey) {
  return PASSIVE_FIELDS.has(fieldKey);
}

/**
 * Determine if a field is an active workflow trigger field.
 *
 * @param {string} fieldKey - The timeline field name
 * @returns {boolean}
 */
export function isFieldActive(fieldKey) {
  return ACTIVE_FIELDS.has(fieldKey);
}

/**
 * Derive the email status to display for a timeline date field.
 *
 * Logic:
 *   - Passive fields → always 'not_applicable'
 *   - Active fields with stored status → use stored status
 *   - Active fields with date but no stored status → 'pending'
 *   - Active fields with no date → 'not_applicable'
 *   - targetDate: passive unless modification occurred (caller manages this)
 *
 * @param {Object} params
 * @param {string} params.fieldKey     - The timeline field name
 * @param {string|null} params.dateValue   - Current date value
 * @param {string|null} params.storedStatus - Stored email status from stageTimeline
 * @returns {string} Email status value
 */
export function deriveEmailStatusFromField({ fieldKey, dateValue, storedStatus }) {
  // Passive fields → always not_applicable
  if (PASSIVE_FIELDS.has(fieldKey)) return 'not_applicable';

  // targetDate is special: it doesn't have a persistent email status marker
  // (its trigger is timeline_change which opens a modal, not an email marker)
  // Treat it as not_applicable for email status purposes
  if (fieldKey === 'targetDate') return 'not_applicable';

  // Active fields with explicit stored status
  if (storedStatus && storedStatus !== 'not_applicable') return storedStatus;

  // Active field with a date value but no stored status → pending
  if (!isEmpty(dateValue)) return 'pending';

  // No date → not_applicable
  return 'not_applicable';
}

// ── Subject line builder ────────────────────────────────────────────────────

/**
 * Build a standardized subject line for timeline emails.
 * All subjects begin with the project code and use underscore separator.
 *
 * @param {string} projectCode - Project code (e.g. "HB")
 * @param {'Stage'|'Invoice'} category - Subject category
 * @param {string} detail - Subject detail text
 * @returns {string} Formatted subject (e.g. "HB_Stage Payment Confirmed")
 */
export function buildTimelineSubject(projectCode, category, detail) {
  const code = projectCode || 'PROJECT';
  return `${code}_${category} ${detail}`;
}
